<template>
  <div class="balance-widget" :class="{ 'balance-widget--insufficient': shouldShowInsufficientAlert }">
    <div v-if="isPhys" class="balance-widget__account">
      <div class="balance-widget__account-label">Лицевой счет №</div>
      <button
        type="button"
        class="balance-widget__account-number"
        :class="{ 'balance-widget__account-number--copied': isAccountCopied }"
        :disabled="!isAccountNumberAvailable"
        :aria-label="isAccountNumberAvailable ? 'Скопировать номер лицевого счета' : 'Номер лицевого счета недоступен'"
        @click="handleCopyAccount"
      >
        <span class="balance-widget__account-number-text">{{ displayNumber }}</span>
        <span
          class="balance-widget__account-copy"
          aria-live="polite"
          :aria-hidden="(!isAccountCopied).toString()"
        >
          Скопировано
        </span>
      </button>
    </div>

    <div class="balance-widget__summary">
      <div class="balance-widget__caption">Текущий баланс</div>
      <div class="balance-widget__amount" :class="{ 'balance-widget__amount--alert': isServiceNotActive }">
        {{ formattedBalance }}
      </div>
      <button
        type="button"
        class="balance-widget__topup"
        :class="{
          'balance-widget__topup--disabled': !paymentsActive
        }"
        :disabled="!paymentsActive"
        @click="handleTopUp"
      >
        Пополнить
      </button>
    </div>

    <div v-if="shouldShowInsufficientAlert" class="balance-widget__warning">
      <span class="balance-widget__warning-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="M21.76,15.92L15.36,4.4C14.5,2.85 13.31,2 12,2C10.69,2 9.5,2.85 8.64,4.4L2.24,15.92C1.43,17.39 1.34,18.8 1.99,19.91C2.64,21.02 3.92,21.63 5.6,21.63H18.4C20.08,21.63 21.36,21.02 22.01,19.91C22.66,18.8 22.57,17.38 21.76,15.92ZM11.25,9C11.25,8.59 11.59,8.25 12,8.25C12.41,8.25 12.75,8.59 12.75,9V14C12.75,14.41 12.41,14.75 12,14.75C11.59,14.75 11.25,14.41 11.25,14V9ZM12.71,17.71C12.66,17.75 12.61,17.79 12.56,17.83C12.5,17.87 12.44,17.9 12.38,17.92C12.32,17.95 12.26,17.97 12.19,17.98C12.13,17.99 12.06,18 12,18C11.94,18 11.87,17.99 11.8,17.98C11.74,17.97 11.68,17.95 11.62,17.92C11.56,17.9 11.5,17.87 11.44,17.83C11.39,17.79 11.34,17.75 11.29,17.71C11.11,17.52 11,17.26 11,17C11,16.74 11.11,16.48 11.29,16.29C11.34,16.25 11.39,16.21 11.44,16.17C11.5,16.13 11.56,16.1 11.62,16.08C11.68,16.05 11.74,16.03 11.8,16.02C11.93,15.99 12.07,15.99 12.19,16.02C12.26,16.03 12.32,16.05 12.38,16.08C12.44,16.1 12.5,16.13 12.56,16.17C12.61,16.21 12.66,16.25 12.71,16.29C12.89,16.48 13,16.74 13,17C13,17.26 12.89,17.52 12.71,17.71Z" />
        </svg>
      </span>
      <span class="balance-widget__warning-text">{{ insufficientWarningText }}</span>
    </div>

    <div class="balance-widget__meta">
      <div v-if="hasRecommend" class="balance-widget__meta-item">
        <span class="balance-widget__meta-label">Рекомендуемый платеж</span>
        <span class="balance-widget__meta-value">{{ formattedRecommendPay }}</span>
      </div>
      <div v-if="hasBonus" class="balance-widget__meta-item">
        <span class="balance-widget__meta-label">Бонусы</span>
        <span class="balance-widget__meta-value">{{ formattedBonus }}</span>
      </div>
    </div>

    <div class="balance-widget__actions">
      <button
        type="button"
        :class="[
          'balance-widget__action',
          'balance-widget__action--auto',
          { 'balance-widget__action--disabled': isAutoPaymentDisabled }
        ]"
        :disabled="isAutoPaymentDisabled"
        @click="handleAutoPayment"
      >
        <span class="balance-widget__action-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" focusable="false">
            <path d="M16.75,3.56V2C16.75,1.59 16.41,1.25 16,1.25C15.59,1.25 15.25,1.59 15.25,2V3.5H8.75V2C8.75,1.59 8.41,1.25 8,1.25C7.59,1.25 7.25,1.59 7.25,2V3.56C4.55,3.81 3.24,5.42 3.04,7.81C3.02,8.1 3.26,8.34 3.54,8.34H20.46C20.75,8.34 20.99,8.09 20.96,7.81C20.76,5.42 19.45,3.81 16.75,3.56Z" />
            <path d="M19,15C16.79,15 15,16.79 15,19C15,19.75 15.21,20.46 15.58,21.06C16.27,22.22 17.54,23 19,23C20.46,23 21.73,22.22 22.42,21.06C22.79,20.46 23,19.75 23,19C23,16.79 21.21,15 19,15ZM21.07,18.57L18.94,20.54C18.8,20.67 18.61,20.74 18.43,20.74C18.24,20.74 18.05,20.67 17.9,20.52L16.91,19.53C16.62,19.24 16.62,18.76 16.91,18.47C17.2,18.18 17.68,18.18 17.97,18.47L18.45,18.95L20.05,17.47C20.35,17.19 20.83,17.21 21.11,17.51C21.39,17.81 21.37,18.28 21.07,18.57Z" />
            <path d="M20,9.84H4C3.45,9.84 3,10.29 3,10.84V17C3,20 4.5,22 8,22H12.93C13.62,22 14.1,21.33 13.88,20.68C13.68,20.1 13.51,19.46 13.51,19C13.51,15.97 15.98,13.5 19.01,13.5C19.3,13.5 19.59,13.52 19.87,13.57C20.47,13.66 21.01,13.19 21.01,12.59V10.85C21,10.29 20.55,9.84 20,9.84ZM9.21,18.21C9.02,18.39 8.76,18.5 8.5,18.5C8.24,18.5 7.98,18.39 7.79,18.21C7.61,18.02 7.5,17.76 7.5,17.5C7.5,17.24 7.61,16.98 7.79,16.79C7.89,16.7 7.99,16.63 8.12,16.58C8.49,16.42 8.93,16.51 9.21,16.79C9.39,16.98 9.5,17.24 9.5,17.5C9.5,17.76 9.39,18.02 9.21,18.21ZM9.21,14.71C9.16,14.75 9.11,14.79 9.06,14.83C9,14.87 8.94,14.9 8.88,14.92C8.82,14.95 8.76,14.97 8.7,14.98C8.63,14.99 8.56,15 8.5,15C8.24,15 7.98,14.89 7.79,14.71C7.61,14.52 7.5,14.26 7.5,14C7.5,13.74 7.61,13.48 7.79,13.29C8.02,13.06 8.37,12.95 8.7,13.02C8.76,13.03 8.82,13.05 8.88,13.08C8.94,13.1 9,13.13 9.06,13.17C9.11,13.21 9.16,13.25 9.21,13.29C9.39,13.48 9.5,13.74 9.5,14C9.5,14.26 9.39,14.52 9.21,14.71ZM12.71,14.71C12.52,14.89 12.26,15 12,15C11.74,15 11.48,14.89 11.29,14.71C11.11,14.52 11,14.26 11,14C11,13.74 11.11,13.48 11.29,13.29C11.67,12.92 12.34,12.92 12.71,13.29C12.89,13.48 13,13.74 13,14C13,14.26 12.89,14.52 12.71,14.71Z" />
          </svg>
        </span>
        <span class="balance-widget__action-title">Автоплатеж</span>
        <span
          class="balance-widget__action-status"
          :class="`balance-widget__action-status--${autoPaymentNormalized}`"
        >
          {{ autoPaymentStatusText }}
        </span>
      </button>
      <button type="button" class="balance-widget__action balance-widget__action--history" @click="handleHistory">
        <span class="balance-widget__action-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" focusable="false">
            <path d="M16,9.75H8C7.31,9.75 6.75,10.31 6.75,11V12.05C6.78,12.04 6.8,12.03 6.83,12.02C6.95,11.96 7.08,11.92 7.21,11.88C7.27,11.86 7.33,11.84 7.4,11.82C7.6,11.78 7.8,11.75 8.01,11.75H16.01C16.22,11.75 16.42,11.78 16.62,11.82C16.68,11.83 16.74,11.86 16.81,11.88C16.94,11.92 17.07,11.96 17.19,12.02C17.22,12.03 17.25,12.04 17.27,12.05V11C17.25,10.31 16.69,9.75 16,9.75Z" />
            <path d="M16,13.25H8C7.31,13.25 6.75,13.81 6.75,14.5V15H9.5C9.91,15 10.25,15.34 10.25,15.75C10.25,16.71 11.04,17.5 12,17.5C12.96,17.5 13.75,16.71 13.75,15.75C13.75,15.34 14.09,15 14.5,15H17.25V14.5C17.25,13.81 16.69,13.25 16,13.25Z" />
            <path d="M16,6.5H8C7.31,6.5 6.75,7.06 6.75,7.75V8.56C7.13,8.37 7.55,8.25 8,8.25H16C16.45,8.25 16.87,8.37 17.25,8.56V7.75C17.25,7.06 16.69,6.5 16,6.5Z" />
            <path d="M16.19,2H7.81C4.17,2 2,4.17 2,7.81V16.18C2,19.83 4.17,22 7.81,22H16.18C19.82,22 21.99,19.83 21.99,16.19V7.81C22,4.17 19.83,2 16.19,2ZM19,16.5H18H15.16C14.82,17.93 13.53,19 12,19C10.47,19 9.18,17.93 8.84,16.5H6H5C4.59,16.5 4.25,16.16 4.25,15.75C4.25,15.34 4.59,15 5,15H5.25V14.5V14V11V7.75C5.25,6.23 6.48,5 8,5H16C17.52,5 18.75,6.23 18.75,7.75V11V14V14.5V15H19C19.41,15 19.75,15.34 19.75,15.75C19.75,16.16 19.41,16.5 19,16.5Z" />
          </svg>
        </span>
        <span class="balance-widget__action-title">История<br>платежей</span>
      </button>
    </div>

    <div class="balance-widget__links">
      <button type="button" class="balance-widget__link" @click="handleBonus">
        История бонусов
      </button>
      <button type="button" class="balance-widget__link" @click="$emit('update')">
        Обновить данные
      </button>
    </div>
  </div>
</template>

<script>
import { normalizeAutopaymentStatus } from "../utils/autopayment";

export default {
  name: 'widget-balance-main',
  props: {
    isPhys: {
      type: Boolean,
      default: true
    },
    number: {
      type: String,
      default: ''
    },
    balance: {
      type: [Number, String],
      default: null
    },
    recommendPay: {
      type: [Number, String, null],
      default: null
    },
    bonus: {
      type: [Number, String, null],
      default: null
    },
    isBalanceInsufficient: {
      type: Boolean,
      default: false
    },
    serviceStatus: {
      type: String,
      default: null
    },
    autoPaymentActive: {
      type: Boolean,
      default: false
    },
    autoPaymentStatus: {
      type: String,
      default: null
    },
    paymentsActive: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      isAccountCopied: false,
      accountCopyTimeout: null
    };
  },
  computed: {
    displayNumber() {
      return this.number && this.number.trim() !== '' ? this.number : '- - - - - - -';
    },
    isAccountNumberAvailable() {
      return Boolean(this.number && this.number.trim() !== '');
    },
    numericBalance() {
      return this.parseNumeric(this.balance);
    },
    normalizedServiceStatus() {
      return typeof this.serviceStatus === 'string' ? this.serviceStatus.trim().toLowerCase() : '';
    },
    isServiceNotActive() {
      return this.normalizedServiceStatus === 'notactive';
    },
    shouldShowInsufficientAlert() {
      return this.isServiceNotActive && this.isBalanceInsufficient;
    },
    formattedBalance() {
      const value = this.numericBalance;
      if (value === null) {
        return '- ₽';
      }
      return this.formatCurrency(value);
    },
    hasRecommend() {
      return this.parseNumeric(this.recommendPay) !== null;
    },
    formattedRecommendPay() {
      const value = this.parseNumeric(this.recommendPay);
      return value === null ? '- ₽' : this.formatCurrency(value);
    },
    warningRecommendedAmount() {
      if (this.hasRecommend) {
        return this.formattedRecommendPay;
      }
      return '1 ₽';
    },
    insufficientWarningText() {
      return `Рекомендуемый платёж для работы услуг не менее ${this.warningRecommendedAmount}`;
    },
    hasBonus() {
      return this.parseNumeric(this.bonus) !== null;
    },
    formattedBonus() {
      const value = this.parseNumeric(this.bonus);
      return value === null ? '-' : `${this.formatNumber(value)} Б`;
    },
    autoPaymentNormalized() {
      return normalizeAutopaymentStatus(this.autoPaymentStatus) ?? (this.autoPaymentActive ? 'active' : 'disabled');
    },
    autoPaymentStatusText() {
      const status = this.autoPaymentNormalized;
      if (status === 'active') {
        return 'Активен';
      }
      if (status === 'available') {
        return 'Доступен';
      }
      if (status === 'disabled') {
        return 'Недоступен';
      }
      return 'Недоступен';
    },
    isAutoPaymentDisabled() {
      return this.autoPaymentNormalized === 'disabled';
    }
  },
  methods: {
    async copyText(text) {
      if (!text) {
        return false;
      }
      if (typeof navigator !== 'undefined' && navigator.clipboard && typeof window !== 'undefined' && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(text);
          return true;
        } catch (error) {
          return false;
        }
      }
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        const result = document.execCommand('copy');
        document.body.removeChild(textarea);
        return result;
      } catch (error) {
        return false;
      }
    },
    async handleCopyAccount() {
      if (!this.isAccountNumberAvailable) {
        return;
      }
      const value = this.number.trim();
      const copied = await this.copyText(value);
      if (!copied) {
        return;
      }
      this.isAccountCopied = true;
      if (this.accountCopyTimeout) {
        clearTimeout(this.accountCopyTimeout);
      }
      this.accountCopyTimeout = setTimeout(() => {
        this.isAccountCopied = false;
        this.accountCopyTimeout = null;
      }, 2000);
    },
    parseNumeric(value) {
      if (value === null || value === undefined || value === '') {
        return null;
      }
      const normalized = String(value).replace(/[^0-9,.-]/g, '').replace(',', '.');
      const parsed = parseFloat(normalized);
      return Number.isNaN(parsed) ? null : parsed;
    },
    formatCurrency(value) {
      return `${new Intl.NumberFormat('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value)} ₽`;
    },
    formatNumber(value) {
      return new Intl.NumberFormat('ru-RU').format(value);
    },
    handleTopUp() {
      if (!this.paymentsActive) {
        return;
      }
      if (this.isPhys) {
        this.$router.push('/balance');
      } else {
        this.$router.push({ path: '/documents', query: { page: '1' } });
      }
    },
    handleHistory() {
      this.$router.push('/balance');
    },
    handleAutoPayment() {
      if (this.isAutoPaymentDisabled) {
        return;
      }
      this.$router.push('/autopayment');
    },
    handleBonus() {
      this.$router.push('/bonus');
    }
  },
  beforeUnmount() {
    if (this.accountCopyTimeout) {
      clearTimeout(this.accountCopyTimeout);
    }
  }
};
</script>

<style scoped>
.balance-widget {
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #ffffff;
  border-radius: 24px;
  padding: 24px;
  box-shadow: 0 12px 32px rgba(43, 89, 120, 0.12);
  border: 1px solid #e9f1f9;
}

.balance-widget__account-label {
  font-family: 'Mont', serif;
  font-weight: 600;
  font-size: 0.9rem;
  color: #6c7b8a;
}

.balance-widget__account {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 4px;
}

.balance-widget__account-number {
  border: none;
  background: none;
  padding: 0;
  text-align: center;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  cursor: pointer;
}

.balance-widget__account-number-text {
  font-family: 'Mont', serif;
  font-weight: 700;
  letter-spacing: 0.25rem;
  color: #24484e;
}

.balance-widget__account-copy {
  font-family: 'Mont', serif;
  font-size: 0.75rem;
  font-weight: 600;
  color: #0082bf;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.balance-widget__account-number--copied .balance-widget__account-copy {
  opacity: 1;
}

.balance-widget__account-number:disabled {
  cursor: default;
}

.balance-widget__account-number:focus-visible {
  outline: 2px solid #0082bf;
  outline-offset: 3px;
  border-radius: 6px;
}

.balance-widget__summary {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  text-align: center;
}

.balance-widget__caption {
  font-family: 'Mont', serif;
  font-weight: 600;
  font-size: 1rem;
  color: #6c7b8a;
}

.balance-widget__amount {
  font-family: 'Mont', serif;
  font-weight: 800;
  font-size: 2.8rem;
  line-height: 1.2;
  color: #24484e;
}

.balance-widget__amount--alert {
  color: #df0e0e;
}

.balance-widget__topup {
  cursor: pointer;
  border: none;
  border-radius: 16px;
  padding: 16px 32px;
  font-family: 'Mont', serif;
  font-weight: 700;
  font-size: 1rem;
  color: #ffffff;
  background: #df0e0e;
  transition: opacity 0.2s ease, transform 0.2s ease;
  width: 100%;
}

.balance-widget__topup:hover {
  opacity: 0.9;
}

.balance-widget__topup:active {
  transform: translateY(1px);
}

.balance-widget__topup--disabled {
  background: #c3ccd8;
  cursor: default;
}

.balance-widget__meta {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  justify-content: center;
}

.balance-widget__meta-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
}

.balance-widget__meta-label {
  font-family: 'Mont', serif;
  font-size: 0.85rem;
  color: #6c7b8a;
  text-align: center;
}

.balance-widget__meta-value {
  font-family: 'Mont', serif;
  font-weight: 700;
  color: #24484e;
  text-align: center;
}

.balance-widget__warning {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: rgba(223, 14, 14, 0.08);
  border-radius: 12px;
}

.balance-widget__warning-icon svg {
  width: 20px;
  height: 20px;
  fill: #df0e0e;
}

.balance-widget__warning-text {
  font-family: 'Mont', serif;
  font-size: 0.95rem;
  color: #df0e0e;
}

.balance-widget__actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
}

.balance-widget__action {
  flex: 1 1 140px;
  border: none;
  border-radius: 16px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  background: #f7fafb;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.balance-widget__action:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 16px rgba(0, 130, 191, 0.1);
}

.balance-widget__action-icon svg {
  width: 32px;
  height: 32px;
  fill: currentColor;
}

.balance-widget__action--auto {
  color: #3f8f9f;
}

.balance-widget__action--disabled {
  background: #eef2f5;
  color: #9aa6b2;
  cursor: default;
  box-shadow: none;
}

.balance-widget__action--disabled:hover {
  transform: none;
  box-shadow: none;
}

.balance-widget__action[disabled] {
  pointer-events: none;
}

.balance-widget__action--history {
  color: #4d61b7;
}

.balance-widget__action-title {
  font-family: 'Mont', serif;
  font-weight: 700;
  font-size: 0.95rem;
  text-align: center;
}

.balance-widget__action-status {
  font-family: 'Mont', serif;
  font-size: 0.85rem;
}

.balance-widget__action-status--disabled {
  color: #9aa6b2;
}

.balance-widget__action-status--available,
.balance-widget__action-status--active {
  color: #0082bf;
}

.balance-widget__links {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
}

.balance-widget__link {
  border: none;
  background: none;
  padding: 0;
  font-family: 'Mont', serif;
  font-weight: 600;
  font-size: 0.95rem;
  color: #0082bf;
  cursor: pointer;
  text-align: center;
}

.balance-widget__link:hover {
  text-decoration: underline;
}
</style>
